<script>
    import { onMount } from "svelte";

    import Loader from "../components/Loader.svelte";
    import AlbumItem from "../components/AlbumItem.svelte";
    import SubscriptionMessage from "../components/SubscriptionMessage.svelte";

    import { currentLocation } from "../stores.js";

    onMount(() => {
        currentLocation.set("export");
    });

    const music = MusicKit.getInstance();

    let initiated = false;

    let loading = false;
    let progressMessage = undefined;

    let songs = [];
    let playlists = [];

    $: [exportableAlbums, otherAlbums] = songs.reduce(([exportable, others], song) => {
        const album = song?.relationships?.albums?.data?.[0];

        const isExportable = (album !== undefined && song.attributes?.playParams?.catalogId !== undefined);
        const collection = isExportable ? exportable : others;

        let found = collection.get(album.id);

        if (found === undefined) {
            found = {
                id: album.id,
                attributes: {
                    name: album.attributes?.name,
                    artistName: album.attributes?.artistName,
                    artwork: album.attributes?.artwork,
                },
                songs: [],
            };
            collection.set(album.id, found);
        }

        found.songs.push(song);
        return [exportable, others];
    }, [new Map(), new Map()]);

    $: exportableAlbumsArray = [...exportableAlbums.values()];
    $: otherAlbumsArray = [...otherAlbums.values()];

    $: exportableAlbumsCount = exportableAlbumsArray.reduce((acc, it) => acc + it.songs.length, 0);
    $: otherAlbumsCount = otherAlbumsArray.reduce((acc, it) => acc + it.songs.length, 0);

    $: [exportablePlaylists, otherPlaylists] = playlists.reduce(([exportable, other], playlist) => {
        exportable.push({
            attributes: {
                name: playlist.attributes?.name,
                description: playlist.attributes?.description?.standard,
            },
            tracks: playlist.tracks,
        });
        return [exportable, other];
    }, [[], []]);

    $: console.log("exportablePlaylists", exportablePlaylists);
    $: console.log("otherPlaylists", otherPlaylists);

    let exportableAlbumsCursor = 50;
    let otherAlbumsCursor = 50;

    $: libraryData = {
        songs: exportableAlbumsArray.flatMap(album => album.songs.map(song => song?.attributes?.playParams?.catalogId)),
        playlists: exportablePlaylists.map(playlist => ({
            attributes: {
                name: playlist.attributes.name,
                description: playlist.attributes.description,
            },
            tracks: playlist.tracks
                .filter(track => track.attributes?.playParams?.catalogId !== undefined)
                .map(track => track.attributes?.playParams?.catalogId)
        })),
    };

    async function exportSongs() {
        let songs = [];
        let requestCount = 0;

        while (true) {
            progressMessage = `Listing Songs (got ${songs.length} over ${requestCount} requests)`;
            const { data: { data, meta: { total } = { total: 0 } } } = await music.api.music(`/v1/me/library/songs`, {
                include: ["albums"],
                offset: songs.length,
                limit: 100,
            });
            console.log(data);
            songs.push(...data);
            requestCount += 1;
            if (data.length == 0 || songs.length >= total) {
                break;
            }
        }

        console.log("songs:", songs);

        return songs;
    }

    async function exportPlaylists() {
        let playlists = [];
        let requestCount = 0;

        while (true) {
            progressMessage = `Fetching Playlists (got ${playlists.length} over ${requestCount} requests)`;
            const { data: { data, meta: { total } = { total: 0 } } } = await music.api.music(`/v1/me/library/playlists`, {
                offset: playlists.length,
                limit: 100,
            });
            console.log(data);
            playlists.push(...data);
            requestCount += 1;
            if (data.length == 0 || playlists.length >= total) {
                break;
            }
        }

        console.log("playlists:", playlists);
        requestCount = 0;

        for (const playlist of playlists) {
            const tracks = [];

            while (true) {
                progressMessage = `Fetching Playlist Tracks (over ${requestCount} requests)`;
                const { data: { data, meta: { total } = { total: 0 } } } = await music.api.music(`/v1/me/library/playlists/${playlist.id}/tracks`, {
                    offset: tracks.length,
                    limit: 100,
                });
                console.log(data);
                tracks.push(...data);
                requestCount += 1;
                if (data.length == 0 || tracks.length >= total) {
                    break;
                }
            }

            playlist.tracks = tracks;
        }

        return playlists;
    }

    function handleLibraryExport() {
        loading = true;
        (async () => {
            try {
                songs = await exportSongs();
                playlists = await exportPlaylists();
                initiated = true;
            } catch (err) {
                console.error(err);
            } finally {
                loading = false;
            }
        })();
    }

    function handleLibraryCopy() {
        const data = JSON.stringify(libraryData);
        navigator.clipboard.writeText(data);
    }
</script>

<div class="root centered">
    {#if music.isAuthorized}
        {#if loading}
            <div class="loader-container vstack centered">
                <div><Loader /></div>
                <div>{progressMessage}</div>
            </div>
        {:else if !initiated}
            <div class="button" on:click={handleLibraryExport}>
                Export Library
            </div>
        {:else}
            <div class="library-container vstack centered">
                <div class="library hstack">
                    <div class="known vstack">
                        <div class="known-title centered">Exportable ({exportableAlbumsCount} songs)</div>
                        <div class="known-items vstack nowrap">
                            {#each exportableAlbumsArray.slice(0, exportableAlbumsCursor) as album (album.id)}
                                <AlbumItem {album} />
                            {/each}
                            {#if exportableAlbumsArray.length > exportableAlbumsCursor}
                                <div class="see-more centered">
                                    <div class="button" on:click={() => exportableAlbumsCursor += 50}>See More</div>
                                </div>
                            {/if}
                        </div>
                    </div>
                    <div class="unknown vstack">
                        <div class="unknown-title centered">Out of Catalog ({otherAlbumsCount} songs)</div>
                        <div class="unknown-items vstack nowrap">
                            {#each otherAlbumsArray.slice(0, otherAlbumsCursor) as album (album.id)}
                                <AlbumItem {album} />
                            {/each}
                            {#if otherAlbumsArray.length > otherAlbumsCursor}
                                <div class="see-more centered">
                                    <div class="button" on:click={() => otherAlbumsCursor += 50}>See More</div>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
                <div class="button" on:click={handleLibraryCopy}>
                    Copy Library
                </div>
            </div>
        {/if}
    {:else}
        <SubscriptionMessage />
    {/if}
</div>

<style>
    .root {
        width: 100%;
        height: 100%;
        position: relative;
        color: var(--app-fg-color);
    }

    .loader-container {
        gap: 20px;
    }

    .button {
        border: solid 2px var(--app-fg-color);
        border-radius: 5px;
        padding: 10px 20px;
        cursor: pointer;
    }

    .button:hover {
        color: var(--app-bg-color);
        background-color: var(--app-fg-color);
    }

    .library-container {
        gap: 10px;
    }

    .library {
        gap: 10px;

        --song-item-padding: 5px;
    }

    .known, .unknown {
        gap: 5px;
    }

    .known-title, .unknown-title {
        font-size: 20px;
        font-weight: bold;
        text-align: center;
        width: 100%;
        padding: 10px;
        border: solid 2px var(--app-fg-color);
        border-radius: 5px;
        box-sizing: border-box;
    }

    .see-more {
        padding: 5px 0;
    }

    .known-items, .unknown-items {
        width: 500px;
        height: 500px;
        gap: 5px;
        overflow-y: auto;
        padding: 10px;
        border: solid 2px var(--app-fg-color);
        border-radius: 5px;
    }
</style>
